import { Component, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { OnboradingService } from '../../onboarding/shared/service/onboarding.service';
import { Table } from '../../shared/table/table';
import { TableAction, TableColumn } from '../../shared/models/table-models';
import { ApiService } from '../../../core/api/api.service';
import { API_HOST } from '../../../core/api/api.config';
import { ENDPOINTS } from '../../../core/api/endpoints';
import { Popup } from '../../../core/shared/components/popup/popup';
import { ToastrService } from 'ngx-toastr';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ENTITY_TYPE, ENTITY_TYPE_NAME } from '../../../core/shared/models/picklist.model';
import { CraditService } from '../../credit/shared/services/cradit.service';
import { Document } from '../../leads/document/document';

interface MyTaskApi {
  aprvlTskKey: number;
  aprvlTskId: string | null;
  aprvlTskDscr: string | null;
  entySeq: number;
  entyTyp: number;
  asgnToUsrSeq: number | null;
  asgnToUsrNm: string | null;
  asgnDt: string | null;
  tskOrdr: number | null;
  stsKey: number;
  crtdBy: string | null;
  crtdDt: string;
  lastUpdBy: string | null;
  lastUpdDt: string;
  delFlg: boolean;
}

interface TaskRow {
  aprvlTskKey: number;
  id: string;
  description: string;

  entityType: number;
  entityTypeLabel: string;

  entySeq: number;
  assignedTo: string;
  assignedDate: string;
  status: number;
  approved: boolean;
}

/** Onboarding service (9191) /tsk/process-task */
interface OnboardingProcessTaskPayload {
  crptOnbrdngSeq: number;
  aprvlTskKey: number;
  actionKey: number;
  cmnt: string;
}

/** Credit service (8989) /credit/tsk/process-task */
interface CreditProcessTaskPayload {
  entySeq: number;
  entyTyp: number;
  aprvlTskKey: number;
  actionKey: number;
  cmmnt: string;
}

interface ProcessTaskRequest {
  host: string;
  payload: OnboardingProcessTaskPayload | CreditProcessTaskPayload;
}

type RejectTaskAction = 'sendBackToRO' | 'permanentReject';
@Component({
  selector: 'app-dashboard-tasks',
  imports: [CommonModule, Table, Popup, FormsModule, Document, TranslatePipe],
  templateUrl: './tasks.html',
  styleUrl: './tasks.css',
})
export class DashboardTasks implements OnInit {
  loading = false;
  error = '';
  isProcessing = false;
  processComment: string = '';

  taskCommentHistory: any[] = [];
  isCommentHistoryLoading = false;
  showConfirmPopup = false;
  popupTitle = 'TASKS.CONFIRM_ACTION';
  popupMessage = '';
  popupMessageParams: Record<string, string> = {};
  confirmText = 'COMMON.CONFIRM';
  cancelText = 'COMMON.CANCEL';
  selectedTask: TaskRow | null = null;
  selectedActionKey: number | null = null;
  readonly APPROVE_ACTION_KEY = 1;
  readonly PERMANENT_REJECT_ACTION_KEY = 2;
  readonly SEND_BACK_TO_RO_ACTION_KEY = 3;

  isRelationshipOfficer = false;

  showRejectActionPopup = false;

  /**
   * Facility Offer Letter tasks have no screen of their own to open, so View
   * lists the letter's documents in a popup instead of routing anywhere.
   * `docsTask` carries the row the popup is showing.
   */
  showDocsPopup = false;
  docsTask: TaskRow | null = null;

  selectedRejectAction: RejectTaskAction = 'sendBackToRO';
  tasks: TaskRow[] = [];

  taskColumns: TableColumn[] = [
    { label: 'TASKS.TASK_ID', key: 'id' },
    { label: 'TASKS.TASK_DESCRIPTION', key: 'description' },
    { label: 'TASKS.ENTITY_TYPE', key: 'entityTypeLabel' },
    { label: 'TASKS.ASSIGNED_DATE', key: 'assignedDate', type: 'date' },
    { label: 'TASKS.ASSIGNED_TO', key: 'assignedTo' },
  ];

  taskActions: TableAction[] = [
    {
      icon: 'images/view.png',
      tooltip: 'COMMON.VIEW',
      onClick: (row: TaskRow) => this.viewTask(row),
    },
    {
      icon: 'images/Tick-2.png',
      tooltip: 'COMMON.APPROVE',
      onClick: (row: TaskRow) => this.openConfirm(row, this.APPROVE_ACTION_KEY),
    },
    {
      icon: 'images/cancel.png',
      tooltip: 'TASKS.REJECT_OR_SEND_BACK',
      onClick: (row: TaskRow) => this.openRejectActionPopup(row),
    },
  ];

  constructor(
    private api: ApiService,
    private toastr: ToastrService,
    private onboarding: OnboradingService,
    private router: Router,
    private creditService: CraditService,
    private translate: TranslateService,
  ) {
    this.translate.onLangChange.pipe(takeUntilDestroyed()).subscribe(() => {
      this.tasks = this.tasks.map((task) => ({
        ...task,
        entityTypeLabel: this.getEntityTypeLabel(task.entityType),
      }));
    });
  }

  /** Display label for an entity type (translated; raw ENTITY_TYPE_NAME kept for reference). */
  private getEntityTypeLabel(entityType: number): string {
    const keys: Record<number, string> = {
      [ENTITY_TYPE.CREDIT_PROPOSAL]: 'TASKS.ENTITY_CREDIT_PROPOSAL',
      [ENTITY_TYPE.FOL]: 'TASKS.ENTITY_FOL',
      [ENTITY_TYPE.PROPOSED_DSBMT]: 'TASKS.ENTITY_PROPOSED_DSBMT',
      [ENTITY_TYPE.ONBOARDING]: 'TASKS.ENTITY_ONBOARDING',
    };
    return keys[entityType]
      ? this.translate.instant(keys[entityType])
      : (ENTITY_TYPE_NAME[entityType] ?? this.translate.instant('TASKS.UNKNOWN_ENTITY', { type: entityType }));
  }

  ngOnInit(): void {
    this.isRelationshipOfficer = this.getCurrentRole() === 'LNLY_RO';

    this.selectedRejectAction = this.isRelationshipOfficer ? 'permanentReject' : 'sendBackToRO';

    this.loadTasks();
  }

  private getCurrentRole(): string {
    return (localStorage.getItem('role') || localStorage.getItem('user_role') || '')
      .trim()
      .toUpperCase();
  }

  loadTasks(): void {
    this.loading = true;
    this.error = '';

    this.api.get<MyTaskApi[]>(API_HOST.tasks, ENDPOINTS.TASKS.GET_MY_TASKS).subscribe({
      next: (res) => {
        const rows = Array.isArray(res) ? res : [];

        this.tasks = rows.map(
          (item): TaskRow => ({
            aprvlTskKey: item.aprvlTskKey,
            id: item.aprvlTskId || `TSK-${item.aprvlTskKey}`,
            description: item.aprvlTskDscr || this.translate.instant('TASKS.TASK_N', { key: item.aprvlTskKey }),

            entityType: Number(item.entyTyp),
            entityTypeLabel: this.getEntityTypeLabel(Number(item.entyTyp)),
            entySeq: item.entySeq,
            assignedTo: item.asgnToUsrNm || '-',
            assignedDate: item.asgnDt || item.crtdDt,
            status: item.stsKey,
            approved: item.stsKey === 1,
          }),
        );

        this.loading = false;
      },
      error: () => {
        this.error = 'TASKS.UNABLE_TO_LOAD';
        this.loading = false;
      },
    });
  }
  private loadTaskCommentHistory(task: TaskRow): void {
    if (!task?.entySeq) {
      this.taskCommentHistory = [];
      return;
    }

    this.isCommentHistoryLoading = true;
    this.taskCommentHistory = [];

    this.api
      .get(
        API_HOST.onboarding,
        `${ENDPOINTS.ONBOARDING.GET_ONBOARDING_REVIEW}/${task.entySeq}/${task.entityType}`,
      )
      .subscribe({
        next: (response: any) => {
          const responseData = response?.data ?? response;

          if (Array.isArray(responseData)) {
            this.taskCommentHistory = responseData;
          } else if (Array.isArray(responseData?.items)) {
            this.taskCommentHistory = responseData.items;
          } else if (Array.isArray(responseData?.reviews)) {
            this.taskCommentHistory = responseData.reviews;
          } else {
            this.taskCommentHistory = [];
          }

          this.taskCommentHistory = [...this.taskCommentHistory].sort(
            (first: any, second: any) =>
              new Date(second.crtdDt ?? 0).getTime() - new Date(first.crtdDt ?? 0).getTime(),
          );

          this.isCommentHistoryLoading = false;
        },

        error: (error) => {
          console.error('Unable to load task comments:', error);

          this.taskCommentHistory = [];
          this.isCommentHistoryLoading = false;
        },
      });
  }
  openRejectActionPopup(task: TaskRow): void {
    if (!task?.aprvlTskKey) {
      this.toastr.error(this.translate.instant('TASKS.INVALID_TASK'));
      return;
    }

    this.selectedTask = task;
    this.selectedActionKey = null;

    this.selectedRejectAction = this.isRelationshipOfficer ? 'permanentReject' : 'sendBackToRO';

    this.showRejectActionPopup = true;
  }

  closeRejectActionPopup(): void {
    this.showRejectActionPopup = false;
    this.selectedTask = null;
    this.selectedActionKey = null;

    this.selectedRejectAction = this.isRelationshipOfficer ? 'permanentReject' : 'sendBackToRO';
  }
  viewTask(task: TaskRow): void {
    if (!task?.entySeq) {
      this.toastr.error(this.translate.instant('TASKS.ENTITY_SEQ_MISSING'));
      return;
    }

    // Same screens the Leads / Credit list "View" actions open.
    if (task.entityType === ENTITY_TYPE.LEADS) {
      this.router.navigate(['/add-new-leads/demographic', task.entySeq], {
        queryParams: { mode: 'view' },
      });
      return;
    }

    if (task.entityType === ENTITY_TYPE.CREDIT_PROPOSAL) {
      this.onboarding.GetOBAprvdList().subscribe({
        next: (res: any) => {
          const list = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
          const match = list.find(
            (row: any) => Number(row?.crdtPrpslSeq) === Number(task.entySeq),
          );
          const onboardingSeq = Number(match?.crptOnbrdngSeq);
          const creditProposalSeq = Number(match?.crdtPrpslSeq);
          if (!onboardingSeq || !creditProposalSeq) {
            this.toastr.error(this.translate.instant('TASKS.CREDIT_PROPOSAL_NOT_FOUND'));
            return;
          }
          this.creditService.setCrdtPrpslSeq(creditProposalSeq);
          this.router.navigate(['/document-builder', onboardingSeq], {
            queryParams: {
              crdtPrpslSeq: creditProposalSeq,
              institutionName: match?.nmOfInstitution || task.description,
              mode: 'view',
            },
          });
        },
        error: () => this.toastr.error(this.translate.instant('TASKS.FAILED_LOAD_CREDIT_PROPOSAL')),
      });
      return;
    }

    // Facility Offer Letter — no dedicated view screen; the approver reviews
    // the letter's uploaded documents in a read-only popup.
    if (task.entityType === ENTITY_TYPE.FOL) {
      this.openDocsPopup(task);
      return;
    }

    if (task.entityType === ENTITY_TYPE.PROPOSED_DSBMT) {
      this.router.navigate(['/disbursement-note', task.entySeq], {
        queryParams: { mode: 'view', fromTask: 'true' },
      });
      return;
    }

    // Onboarding (aur baqi entity types) ka view — onboarding wizard view mode.
    this.router.navigate(['/onboarding/corporate', task.entySeq], {
      queryParams: {
        mode: 'view',
        fromTask: 'true',
      },
    });
  }
  openDocsPopup(task: TaskRow): void {
    this.docsTask = task;
    this.showDocsPopup = true;
  }

  closeDocsPopup(): void {
    this.showDocsPopup = false;
    this.docsTask = null;
  }

  /**
   * FOL tasks go back to the Account Manager; Onboarding and Credit Proposal
   * (every other entity type) go back to the Relationship Officer.
   */
  get isSendBackToAm(): boolean {
    return this.selectedTask?.entityType === ENTITY_TYPE.FOL;
  }

  get sendBackLabel(): string {
    return this.isSendBackToAm ? 'TASKS.SEND_BACK_TO_AM' : 'TASKS.SEND_BACK_TO_RO';
  }

  get sendBackHint(): string {
    return this.isSendBackToAm ? 'TASKS.SEND_BACK_TO_AM_HINT' : 'TASKS.SEND_BACK_TO_RO_HINT';
  }

  prepareRejectConfirmation(): void {
    if (!this.selectedTask) {
      return;
    }

    if (this.isRelationshipOfficer) {
      this.selectedRejectAction = 'permanentReject';
    }

    const isSendBack = this.selectedRejectAction === 'sendBackToRO';

    this.selectedActionKey = isSendBack
      ? this.SEND_BACK_TO_RO_ACTION_KEY
      : this.PERMANENT_REJECT_ACTION_KEY;

    const taskLabel = this.selectedTask.id || `TSK-${this.selectedTask.aprvlTskKey}`;

    this.popupTitle = isSendBack ? this.sendBackLabel : 'TASKS.PERMANENT_REJECT';

    this.popupMessage = isSendBack
      ? this.isSendBackToAm
        ? 'TASKS.CONFIRM_SEND_BACK_AM_MSG'
        : 'TASKS.CONFIRM_SEND_BACK_MSG'
      : 'TASKS.CONFIRM_PERMANENT_REJECT_MSG';
    this.popupMessageParams = { task: taskLabel };

    this.confirmText = isSendBack ? 'COMMON.SEND_BACK' : 'COMMON.REJECT';
    this.cancelText = 'COMMON.CANCEL';
    this.processComment = '';
    this.loadTaskCommentHistory(this.selectedTask);
    this.showRejectActionPopup = false;
    this.showConfirmPopup = true;
  }
  closeConfirmPopup(): void {
    this.showConfirmPopup = false;
    this.selectedTask = null;
    this.selectedActionKey = null;
    this.processComment = '';
    this.taskCommentHistory = [];
    this.isCommentHistoryLoading = false;
  }

  openConfirm(task: TaskRow, actionKey: number): void {
    this.selectedTask = task;
    this.selectedActionKey = actionKey;
    this.processComment = '';

    this.loadTaskCommentHistory(task);

    const isApprove = actionKey === this.APPROVE_ACTION_KEY;

    const taskLabel = task.id || `TSK-${task.aprvlTskKey}`;

    this.popupTitle = isApprove ? 'TASKS.APPROVE_TASK' : 'TASKS.REJECT_TASK';

    this.popupMessage = isApprove
      ? 'TASKS.CONFIRM_APPROVE_MSG'
      : 'TASKS.CONFIRM_REJECT_MSG';
    this.popupMessageParams = { task: taskLabel };

    this.confirmText = isApprove ? 'COMMON.APPROVE' : 'COMMON.REJECT';

    this.cancelText = 'COMMON.CANCEL';
    this.showConfirmPopup = true;
  }

  /*
   * Onboarding tasks onboarding service (9191) process karti hai,
   * baqi entity types credit service (8989) par. Dono ka payload
   * bhi alag hai — credit wala entySeq/entyTyp/cmmnt leta hai.
   */
  private buildProcessTaskRequest(
    task: TaskRow,
    actionKey: number,
    comment: string,
  ): ProcessTaskRequest {
    if (task.entityType === ENTITY_TYPE.ONBOARDING) {
      return {
        host: API_HOST.tasks,
        payload: {
          crptOnbrdngSeq: task.entySeq,
          aprvlTskKey: task.aprvlTskKey,
          actionKey,
          cmnt: comment,
        },
      };
    }

    return {
      host: API_HOST.credit_tasks,
      payload: {
        entySeq: task.entySeq,
        entyTyp: task.entityType,
        aprvlTskKey: task.aprvlTskKey,
        actionKey,
        cmmnt: comment,
      },
    };
  }

  /**
   * Comment ab seedha process-task ke payload mein jata hai —
   * saveOnboardingReview ki alag call ki zaroorat nahi.
   */
  confirmProcessTask(): void {
    if (!this.selectedTask || this.selectedActionKey === null) {
      return;
    }

    if (!this.processComment.trim()) {
      this.toastr.warning(this.translate.instant('TASKS.PLEASE_ADD_COMMENT'));
      return;
    }

    this.isProcessing = true;

    const processRequest = this.buildProcessTaskRequest(
      this.selectedTask,
      this.selectedActionKey,
      this.processComment.trim(),
    );

    this.api
      .post(processRequest.host, ENDPOINTS.TASKS.PROCESS_TASK, processRequest.payload)
      .subscribe({
      next: (processRes: any) => {
        if (processRes?.error || processRes?.success === false || processRes?.status === false) {
          this.toastr.error(processRes?.message || processRes?.error || this.translate.instant('TASKS.FAILED_TO_PROCESS'));

          this.isProcessing = false;
          return;
        }

        this.toastr.success(processRes?.message || this.translate.instant('TASKS.PROCESSED_SUCCESS'));

        this.isProcessing = false;
        this.closeConfirmPopup();

        this.loadTasks();
      },
      error: (err: any) => {
        this.toastr.error(err?.error?.message || err?.error?.error || this.translate.instant('TASKS.FAILED_TO_PROCESS'));

        this.isProcessing = false;
      },
    });
  }
}
